/**
 * PROP-E.2 — the branded public proposal viewer's pure decisions.
 *
 * WHY THIS IS A LIB AND NOT PART OF THE PAGE
 * ------------------------------------------
 * `frontend/vitest.config.ts` includes only `src/**\/*.test.ts` and runs `environment: 'node'` — no
 * jsdom, no @testing-library. Nothing rendered by a component is reachable by a test in this project.
 * So the parsing and the copy choice live here, and the page renders them. (Same reason SD-7.9 extracted
 * `proposalPresentation`.)
 *
 * FAIL CLOSED ON ANYTHING UNRECOGNISED
 * ------------------------------------
 * This page is unauthenticated and driven by an opaque token from the URL bar. An unparseable or
 * unexpected payload must render "this link isn't valid" and MUST NOT render a document — the same rule
 * `normaliseOpsHealth` follows for the ops surface, for the same reason: a permissive default here would
 * show something to someone we could not confirm should see it.
 */

/** The four statuses `get_proposal_share_context` returns. Anything else is treated as `invalid`. */
export type ProposalShareStatus = 'ok' | 'invalid' | 'revoked' | 'unavailable';

export interface ShareTenantBranding {
  company_name: string;
  logo: string | null;
  theme_id: string | null;
  custom_themes: unknown[];
}

export interface SharedProposalSummary {
  name: string | null;
  created_at: string | null;
  design_version_no: number | null;
}

export type ProposalShareContext =
  | { status: 'ok'; proposal: SharedProposalSummary; tenant: ShareTenantBranding }
  | { status: 'invalid' | 'revoked' | 'unavailable' };

const NOT_OK: ProposalShareStatus[] = ['invalid', 'revoked', 'unavailable'];

function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/**
 * Coerce the RPC's jsonb into the context union.
 *
 * An `ok` payload MISSING its proposal or tenant object degrades to `invalid` rather than rendering a
 * half-branded page with a document in it — an `ok` we cannot fully read is not an `ok`.
 */
export function parseShareContext(raw: unknown): ProposalShareContext {
  if (!raw || typeof raw !== 'object') return { status: 'invalid' };
  const obj = raw as Record<string, unknown>;
  const status = obj.status;

  if (typeof status !== 'string') return { status: 'invalid' };
  if (NOT_OK.includes(status as ProposalShareStatus)) {
    return { status: status as 'invalid' | 'revoked' | 'unavailable' };
  }
  if (status !== 'ok') return { status: 'invalid' };

  const p = obj.proposal;
  const t = obj.tenant;
  if (!p || typeof p !== 'object' || !t || typeof t !== 'object') return { status: 'invalid' };

  const pr = p as Record<string, unknown>;
  const tn = t as Record<string, unknown>;

  return {
    status: 'ok',
    proposal: {
      name: str(pr.name),
      created_at: str(pr.created_at),
      design_version_no:
        typeof pr.design_version_no === 'number'
          ? pr.design_version_no
          : typeof pr.design_version_no === 'string' && pr.design_version_no.trim() !== ''
            ? Number(pr.design_version_no)
            : null,
    },
    tenant: {
      // The RPC already COALESCEs this, but a payload that lost it must still name someone.
      company_name: str(tn.company_name) ?? 'The sender',
      logo: str(tn.logo),
      theme_id: str(tn.theme_id),
      custom_themes: Array.isArray(tn.custom_themes) ? tn.custom_themes : [],
    },
  };
}

export interface ShareViewCopy {
  headline: string;
  body: string;
  /** Only ever true for `ok`. The page must not render the document on any other status. */
  showsDocument: boolean;
}

/**
 * What the customer reads.
 *
 * NONE OF THESE SAY "ERROR". Three of the four are ordinary, expected outcomes — a link that was
 * revoked, a proposal not finished yet, a mistyped URL — and calling any of them an error tells the
 * customer something is broken and invites a support call about nothing.
 *
 * `revoked` is worded distinctly from `invalid` on purpose: the customer should know to ask for a new
 * link rather than assume the URL is mangled.
 */
export function shareViewCopy(status: ProposalShareStatus, companyName?: string): ShareViewCopy {
  const who = companyName && companyName.trim() ? companyName.trim() : 'the sender';

  switch (status) {
    case 'ok':
      return { headline: '', body: '', showsDocument: true };
    case 'revoked':
      return {
        headline: 'This proposal is no longer shared',
        body: `${who} has turned off this link. Get in touch with them for an up-to-date copy.`,
        showsDocument: false,
      };
    case 'unavailable':
      return {
        headline: 'This proposal isn’t ready yet',
        body: `The link works, but ${who} hasn’t finished preparing the document. Try again shortly.`,
        showsDocument: false,
      };
    case 'invalid':
    default:
      return {
        headline: 'This link isn’t valid',
        body: 'Double-check the address, or ask whoever sent it for a new link.',
        showsDocument: false,
      };
  }
}
